import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Data } from "grommet";
import React, { useEffect, useRef, useState } from "react";
import { Line, Pie } from "react-chartjs-2";
import { HiDownload } from "react-icons/hi";

import { useGetStateLookups } from "@/api/use-states";
import { Button } from "@/components-new/button";
import { Card } from "@/components-new/card";
import { Field } from "@/components-new/field";
import { Loader } from "@/components-new/loader";
import {
  SearchableListbox,
  SearchableListboxOption,
} from "@/components-new/searchable-listbox";
import { SectionDescription, SectionHeading } from "@/components-new/section";

import { useMarketShareBaskets } from "../api/use-market-share-baskets";
import { useMarketShareData } from "../api/use-market-share-data";
import { useMarketShareDrugs } from "../api/use-market-share-drugs";
import { useMarketShareExport } from "../api/use-market-share-export";
import { useUtilizationTypes } from "../api/use-utilization-types";
import { MarketShareDrug, MarketShareParams } from "../types/market-share";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

const UTILIZATION_PERIODS = [
  "1 Quarter",
  "2 Quarters",
  "3 Quarters",
  "4 Quarters",
  "5 Quarters",
  "6 Quarters",
  "7 Quarters",
  "8 Quarters",
  "1 Year",
  "2 Years",
];

// Create stable time period options for SearchableListbox
const TIME_PERIOD_OPTIONS = UTILIZATION_PERIODS.map((period) => ({
  value: period,
  label: period,
}));

// Helper function to convert plural display labels to singular API format
const convertToSingularForAPI = (period: string): string => {
  // Convert plural forms to singular for API calls
  if (period.endsWith(" Quarters")) {
    return period.replace(" Quarters", " Quarter");
  }
  if (period.endsWith(" Years")) {
    return period.replace(" Years", " Year");
  }
  return period; // Already singular or other format
};

interface MarketShareReportProps {
  // Optional pre-selected drug (for drug-specific views)
  preSelectedDrugId?: number;
  preSelectedDrugName?: string;

  // UI configuration
  showDrugSelection?: boolean;
  showFullReportLink?: boolean;
  title?: string;
  description?: string;

  // Layout
  className?: string;
}

export const MarketShareReport: React.FC<MarketShareReportProps> = ({
  preSelectedDrugId,
  preSelectedDrugName = "",
  showDrugSelection = true,
  showFullReportLink = false,
  title = "Market Share Report",
  description = "Analyze market share data across different drugs, states, and time periods",
  className = "",
}) => {
  const [params, setParams] = useState<MarketShareParams>({
    drugId: preSelectedDrugId || 0,
    marketBasketId: 0,
    stateCode: "",
    utilizationPeriod: "",
    utilizationTypeId: 0,
  });

  // State for pie chart quarter selection
  const [selectedPieChartQuarter, setSelectedPieChartQuarter] =
    useState<string>("");

  const { data: utilizationTypes, isLoading: utilizationTypesLoading } =
    useUtilizationTypes();
  const { data: states, isLoading: statesLoading } = useGetStateLookups();
  const { data: marketShareDrugs, isLoading: marketShareDrugsLoading } =
    useMarketShareDrugs();
  const { data: marketShareBaskets, isLoading: marketShareBasketsLoading } =
    useMarketShareBaskets();

  const [selectedMarketBasketName, setSelectedMarketBasketName] =
    useState<string>("");
  const [hiddenLineChartDrugNames, setHiddenLineChartDrugNames] = useState<
    Set<string>
  >(new Set());
  const [hiddenPieChartDrugNames, setHiddenPieChartDrugNames] = useState<
    Set<string>
  >(new Set());
  const originalLineDataRef = useRef<number[][]>([]);
  const originalPieDataRef = useRef<number[]>([]);

  // Validate that the pre-selected drug is available in market share data
  const isPreSelectedDrugAvailable = marketShareDrugs
    ? marketShareDrugs.some((drug) => drug.id === preSelectedDrugId)
    : null; // null means we're still loading

  // Set the pre-selected drug ID when it changes, but only if it's available
  useEffect(() => {
    if (preSelectedDrugId && preSelectedDrugId !== params.drugId) {
      // Only set if the drug is available in market share data, or if we're still loading
      if (
        isPreSelectedDrugAvailable === true ||
        isPreSelectedDrugAvailable === null
      ) {
        setParams((prev) => ({ ...prev, drugId: preSelectedDrugId }));
      }
    }
  }, [preSelectedDrugId, params.drugId, isPreSelectedDrugAvailable]);

  // Check if utilizationTypeId is a valid selection (including 0 for FFS)
  const hasValidUtilizationType = utilizationTypes
    ? utilizationTypes.some((type) => type.id === params.utilizationTypeId)
    : false;

  // For drug-specific views, check if the drug is available for market share analysis
  const isDrugSpecificView = !!preSelectedDrugId && !showDrugSelection;
  const isDrugNotAvailable =
    isDrugSpecificView && isPreSelectedDrugAvailable === false;

  const canFetchData =
    params.marketBasketId > 0 &&
    !!params.stateCode &&
    hasValidUtilizationType &&
    !!params.utilizationPeriod;

  // Convert plural display format to singular API format
  const apiParams = React.useMemo(
    () => ({
      ...params,
      utilizationPeriod: convertToSingularForAPI(params.utilizationPeriod),
    }),
    [params],
  );

  const {
    data: marketShareData,
    isLoading: dataLoading,
    error,
  } = useMarketShareData(apiParams, canFetchData);
  const { mutate: exportMarketShare, isPending: isExporting } =
    useMarketShareExport();

  const updateParam = <K extends keyof MarketShareParams>(
    key: K,
    value: MarketShareParams[K],
  ) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setParams({
      drugId: 0,
      marketBasketId: 0,
      stateCode: "",
      utilizationPeriod: "",
      utilizationTypeId: 0,
    });
  };

  const handleExport = () => {
    if (!canFetchData) return;

    exportMarketShare({
      ...apiParams,
      marketBasketName: selectedMarketBasketName,
      excludedDrugNames: Array.from(hiddenLineChartDrugNames),
    });
  };

  // Find selected entities for SearchableListbox
  const selectedDrug =
    marketShareDrugs?.find((drug) => drug.id === params.drugId) || null;
  const selectedMarketBasket =
    marketShareBaskets?.find((mb) => mb.id === params.marketBasketId) || null;
  const selectedState =
    states?.find((state) => state.code === params.stateCode) || null;
  const selectedUtilizationType =
    utilizationTypes?.find((type) => type.id === params.utilizationTypeId) ||
    null;
  const selectedTimePeriod =
    TIME_PERIOD_OPTIONS.find(
      (option) => option.value === params.utilizationPeriod,
    ) || null;

  // Generate colors for each drug - shared between line and pie charts
  const drugColorMap = React.useMemo(() => {
    if (!marketShareData?.quarters || marketShareData.quarters.length === 0)
      return new Map<number, string>();

    // Get all unique drugs from all quarters
    const allDrugs = new Map<number, string>();
    marketShareData.quarters.forEach((quarter) => {
      quarter.drugs.forEach((drug) => {
        allDrugs.set(drug.drugId, drug.drugName);
      });
    });

    // Generate colors for each drug
    const generateColors = (count: number) => {
      const baseColors = [
        "#00314C", // Artia Blue
        "#00B1B1", // Artia Green
        "#FD8668", // Artia Coral
        "#FDB26B", // Artia Peach
        "#E7E7E5", // Artia Gray
        "#99ADB7", // Artia Blue 3
        "#99E0E0", // Artia Green 3
        "#FECFC3", // Artia Coral 3
        "#FEE0C4", // Artia Peach 3
        "#5C5C5C", // Artia Gray 3
        "#00141E", // Artia Blue 4
        "#CCEFEF", // Artia Green 4
        "#FFE7E1", // Artia Coral 4
        "#FFF0E1", // Artia Peach 4
        "#668394", // Artia Blue 2
        "#66D0D0", // Artia Green 2
        "#FEB6A4", // Artia Coral 2
        "#FED1A6", // Artia Peach 2
        "#8B8B89", // Artia Gray 2
        "#335A70", // Artia Blue 1
        "#33C1C1", // Artia Green 1
        "#FD9E86", // Artia Coral 1
        "#FDC189", // Artia Peach 1
        "#B9B9B7", // Artia Gray 1
      ];

      const colors = [];
      for (let i = 0; i < count; i++) {
        if (i < baseColors.length) {
          colors.push(baseColors[i]);
        } else {
          // Generate additional colors using HSL
          const hue = (i * 137.508) % 360; // Golden angle approximation
          const saturation = 65 + (i % 3) * 15; // Vary saturation 65-95%
          const lightness = 45 + (i % 4) * 10; // Vary lightness 45-75%
          colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
        }
      }
      return colors;
    };

    const colors = generateColors(allDrugs.size);

    // Create a map of drugId -> color, sorted by drug name to ensure consistency
    const colorMap = new Map<number, string>();
    Array.from(allDrugs.entries())
      .sort(([, aDrugName], [, bDrugName]) =>
        aDrugName.localeCompare(bDrugName),
      )
      .forEach(([drugId], index) => {
        colorMap.set(drugId, colors[index % colors.length]);
      });

    return colorMap;
  }, [marketShareData]);

  const sortedQuarters = React.useMemo(() => {
    if (!marketShareData?.quarters) return [];
    return [...marketShareData.quarters].sort((a, b) => {
      if (a.year === b.year) {
        if (a.quarter === null) return 1;
        if (b.quarter === null) return -1;
        return (a.quarter || 0) - (b.quarter || 0);
      }
      return a.year - b.year;
    });
  }, [marketShareData]);

  // Reset interactive state whenever a new data set is loaded
  useEffect(() => {
    setHiddenLineChartDrugNames(new Set());
    setHiddenPieChartDrugNames(new Set());
    setSelectedPieChartQuarter("");
  }, [marketShareData]);

  // Prepare chart data
  const chartData = React.useMemo(() => {
    if (!sortedQuarters.length) return null;

    const labels = sortedQuarters.map((q) =>
      q.quarter === null ? `Y${q.year}` : `Q${q.quarter} ${q.year}`,
    );

    // Get all unique drugs from all quarters
    const allDrugs = new Map<number, string>();
    sortedQuarters.forEach((quarter) => {
      quarter.drugs.forEach((drug) => {
        allDrugs.set(drug.drugId, drug.drugName);
      });
    });

    // For single quarter, duplicate the data point to create a visible line
    const isSingleQuarter = sortedQuarters.length === 1;

    // Create datasets for each drug, ordered by name for consistency
    const datasets = Array.from(allDrugs.entries())
      .sort(([, aDrugName], [, bDrugName]) =>
        aDrugName.localeCompare(bDrugName),
      )
      .map(([drugId, drugName]) => {
        let data = sortedQuarters.map((quarter) => {
          const drug = quarter.drugs.find((d) => d.drugId === drugId);
          return drug?.marketShare || 0;
        });

        // Suppressed and missing points both plot at 0%, so track which are suppressed
        // and style them apart below.
        let suppressedPoints = sortedQuarters.map((quarter) => {
          const drug = quarter.drugs.find((d) => d.drugId === drugId);
          return drug?.isSuppressionUsed ?? false;
        });

        if (isSingleQuarter && data.length === 1) {
          data = [data[0], data[0]];
          suppressedPoints = [suppressedPoints[0], suppressedPoints[0]];
        }

        const color = drugColorMap.get(drugId) || "#000000";
        const basePointRadius = isSingleQuarter ? 8 : 4;

        return {
          label: drugName,
          data,
          suppressedPoints,
          borderColor: color,
          backgroundColor: color.includes("rgb(")
            ? color.replace("rgb(", "rgba(").replace(")", ", 0.1)")
            : color.replace("hsl(", "hsla(").replace(")", ", 0.1)"),
          tension: 0.4,
          fill: false,
          // Suppressed points render hollow: white fill, coloured ring
          pointRadius: suppressedPoints.map((suppressed) =>
            suppressed ? basePointRadius + 1 : basePointRadius,
          ),
          pointHoverRadius: isSingleQuarter ? 10 : 6,
          pointBackgroundColor: suppressedPoints.map((suppressed) =>
            suppressed ? "#ffffff" : color,
          ),
          pointBorderColor: color,
          pointBorderWidth: suppressedPoints.map((suppressed) =>
            suppressed ? 2 : 1,
          ),
        };
      });

    originalLineDataRef.current = datasets.map((d) => [...d.data]);

    const finalLabels =
      isSingleQuarter && labels.length === 1 ? [labels[0], labels[0]] : labels;

    return { labels: finalLabels, datasets };
  }, [sortedQuarters, drugColorMap]);

  // Prepare pie chart data for selected quarter
  const pieChartData = React.useMemo(() => {
    if (!marketShareData?.quarters || !selectedPieChartQuarter) return null;

    const selectedQuarter = marketShareData.quarters.find((q) => {
      const quarterLabel =
        q.quarter === null ? `Y${q.year}` : `Q${q.quarter} ${q.year}`;
      return quarterLabel === selectedPieChartQuarter;
    });

    if (!selectedQuarter || selectedQuarter.drugs.length === 0) return null;

    // Sort drugs by name for consistency with line chart
    const sortedDrugs = [...selectedQuarter.drugs].sort((a, b) =>
      a.drugName.localeCompare(b.drugName),
    );

    // For too many drugs, group smaller ones into "Others"
    const maxSlices = 20; // Show top 20 drugs individually
    let drugsToShow = sortedDrugs;
    let othersData = null;

    if (sortedDrugs.length > maxSlices) {
      drugsToShow = sortedDrugs.slice(0, maxSlices - 1);
      const otherDrugs = sortedDrugs.slice(maxSlices - 1);
      const othersTotal = otherDrugs.reduce(
        (sum, drug) => sum + (drug.marketShare || 0),
        0,
      );

      if (othersTotal > 0) {
        othersData = {
          drugName: `Others (${otherDrugs.length} drugs)`,
          marketShare: othersTotal,
        };
      }
    }

    const labels = drugsToShow.map((drug) => drug.drugName);
    const data = drugsToShow.map((drug) => drug.marketShare || 0);
    // Use the same color map as the line chart based on drugId
    const colors = drugsToShow.map(
      (drug) => drugColorMap.get(drug.drugId) || "#000000",
    );

    if (othersData) {
      labels.push(othersData.drugName);
      data.push(othersData.marketShare);
      colors.push("rgb(156, 163, 175)"); // Gray for others
    }

    if (data.length === 0) return null;
    // Chart.js draws nothing when every slice is zero, which is what a fully suppressed
    // period produces. Bail out so the suppressed panel renders instead.
    if (data.reduce((sum, value) => sum + value, 0) === 0) return null;

    originalPieDataRef.current = [...data];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.map((color) =>
            color.includes("rgb(")
              ? color.replace("rgb(", "rgba(").replace(")", ", 0.8)")
              : color.replace("hsl(", "hsla(").replace(")", ", 0.8)"),
          ),
          borderColor: colors,
          borderWidth: 2,
        },
      ],
    };
  }, [marketShareData, selectedPieChartQuarter, drugColorMap]);

  // The period the pie chart is showing has rows, but every one of them is suppressed
  const selectedQuarterAllSuppressed = React.useMemo(() => {
    if (!marketShareData?.quarters || !selectedPieChartQuarter) return false;
    const selectedQuarter = marketShareData.quarters.find((q) => {
      const label =
        q.quarter === null ? `Y${q.year}` : `Q${q.quarter} ${q.year}`;
      return label === selectedPieChartQuarter;
    });
    return (
      !!selectedQuarter &&
      selectedQuarter.drugs.length > 0 &&
      selectedQuarter.isSuppressionUsed
    );
  }, [marketShareData, selectedPieChartQuarter]);

  // Every period returned is fully suppressed, so the report has no share to show anywhere
  const allPeriodsSuppressed = React.useMemo(() => {
    if (!sortedQuarters.length) return false;
    return sortedQuarters.every(
      (quarter) => quarter.drugs.length > 0 && quarter.isSuppressionUsed,
    );
  }, [sortedQuarters]);

  // Top performer for the selected quarter, recalculated as visible drugs change
  const dynamicTopPerformer = React.useMemo(() => {
    if (!marketShareData?.quarters || !selectedPieChartQuarter) return null;
    const selectedQuarter = marketShareData.quarters.find((q) => {
      const label =
        q.quarter === null ? `Y${q.year}` : `Q${q.quarter} ${q.year}`;
      return label === selectedPieChartQuarter;
    });
    if (!selectedQuarter) return null;
    const visibleDrugs = selectedQuarter.drugs.filter(
      (d) => !hiddenPieChartDrugNames.has(d.drugName),
    );
    if (visibleDrugs.length === 0) return null;
    // All shares are zero when every visible drug is suppressed, so ranking them would
    // crown an arbitrary drug
    if (visibleDrugs.every((d) => d.isSuppressionUsed)) return null;
    const visibleTotal = visibleDrugs.reduce(
      (sum, d) => sum + (d.marketShare || 0),
      0,
    );
    const top = [...visibleDrugs].sort(
      (a, b) => b.marketShare - a.marketShare,
    )[0];
    return {
      drugName: top.drugName,
      marketShare:
        visibleTotal === 0 ? 0 : (top.marketShare / visibleTotal) * 100,
    };
  }, [marketShareData, selectedPieChartQuarter, hiddenPieChartDrugNames]);

  // Available quarters for pie chart selection
  const availableQuarters = React.useMemo(() => {
    return sortedQuarters.map((q) =>
      q.quarter === null ? `Y${q.year}` : `Q${q.quarter} ${q.year}`,
    );
  }, [sortedQuarters]);

  // Auto-select the latest quarter for pie chart
  React.useEffect(() => {
    if (availableQuarters.length > 0 && !selectedPieChartQuarter) {
      setSelectedPieChartQuarter(
        availableQuarters[availableQuarters.length - 1],
      );
    }
  }, [availableQuarters, selectedPieChartQuarter]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        display: chartData?.datasets.length
          ? chartData.datasets.length <= 10
          : true,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 15,
          font: {
            size: 11,
          },
        },
        onClick(e: any, legendItem: any, legend: any) {
          ChartJS.defaults.plugins.legend.onClick.call(
            this as any,
            e,
            legendItem,
            legend,
          );
          const chart = legend.chart;

          // Determine visibility after Chart.js has toggled the clicked dataset
          const visibleMask = chart.data.datasets.map((_: any, idx: number) =>
            chart.isDatasetVisible(idx),
          );

          const newHidden = new Set<string>();
          chart.data.datasets.forEach((dataset: any, idx: number) => {
            if (!visibleMask[idx]) newHidden.add(dataset.label);
          });
          setHiddenLineChartDrugNames(newHidden);

          // Recalculate totals per quarter from the original (unmodified) values
          const quarterCount = originalLineDataRef.current[0]?.length ?? 0;
          const visibleTotalsByQuarter = new Array<number>(quarterCount).fill(
            0,
          );
          originalLineDataRef.current.forEach((datasetData, dsIdx) => {
            if (visibleMask[dsIdx]) {
              datasetData.forEach((val, qIdx) => {
                visibleTotalsByQuarter[qIdx] += val;
              });
            }
          });

          // Normalize each dataset's points relative to the visible total for that quarter
          chart.data.datasets = chart.data.datasets.map(
            (dataset: any, dsIdx: number) => {
              dataset.data = originalLineDataRef.current[dsIdx].map(
                (originalVal, qIdx) => {
                  const total = visibleTotalsByQuarter[qIdx];
                  return total === 0 ? 0 : (originalVal / total) * 100;
                },
              );
              return dataset;
            },
          );

          chart.update();
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        callbacks: {
          label: function (context: any) {
            if (context.dataset.suppressedPoints?.[context.dataIndex]) {
              return `${context.dataset.label}: Suppressed`;
            }
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,

        ticks: {
          callback: function (value: any) {
            return value + "%";
          },
        },
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 15,
          font: {
            size: 11,
          },
        },
        onClick(e: any, legendItem: any, legend: any) {
          ChartJS.overrides.pie.plugins.legend.onClick.call(
            this as any,
            e,
            legendItem,
            legend,
          );
          const chart = legend.chart;

          // Track hidden drug names for analytics recalculation
          const newHiddenPie = new Set<string>();
          legend.legendItems.forEach((item: any) => {
            if (item.hidden) newHiddenPie.add(item.text);
          });
          setHiddenPieChartDrugNames(newHiddenPie);

          // Recalculate visible total from the original (unmodified) values
          const visibleTotal = originalPieDataRef.current.reduce(
            (sum, val, i) => (legend.legendItems[i]?.hidden ? sum : sum + val),
            0,
          );

          chart.data.datasets[0].data = originalPieDataRef.current.map(
            (originalVal) =>
              visibleTotal === 0 ? 0 : (originalVal / visibleTotal) * 100,
          );

          chart.update();
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.parsed;
            return `${label}: ${value.toFixed(2)}%`;
          },
        },
      },
    },
  };

  const displayTitle = preSelectedDrugName
    ? `${title} for ${preSelectedDrugName}`
    : title;
  const displayDescription = preSelectedDrugName
    ? `Analyze market share data for ${preSelectedDrugName} across different states and time periods`
    : description;

  return (
    <div className={className}>
      <SectionHeading level={2}>{displayTitle}</SectionHeading>
      <SectionDescription>
        Select a market basket to view detailed Medicaid utilization insights
        for tracked drugs, including your products and key competitors. If your
        organization is looking to track additional drugs, please contact{" "}
        <a href="mailto:art_capsule@petauri.com">art_capsule@petauri.com</a>.
      </SectionDescription>

      {isDrugNotAvailable ? (
        <Card className="mt-8">
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-yellow-100">
              <svg
                className="size-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Market Share Data Not Available
            </h3>
            <p className="mb-4 text-gray-600">
              Market share analysis is not currently available for{" "}
              {preSelectedDrugName}. This drug is not included in our market
              share reporting dataset.
            </p>
            <p className="text-sm text-gray-500">
              Please check back later as we regularly update our data coverage,
              or contact support if you believe this is an error.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="mt-8">
            <SectionHeading level={3}>Selection</SectionHeading>
            <div
              className={`mt-6 grid grid-cols-1 items-end gap-4 ${showDrugSelection ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}
            >
              {showDrugSelection && (
                <Field label="Market Baskets">
                  {marketShareDrugsLoading ? (
                    <SearchableListbox
                      value={null}
                      onChange={() => {}}
                      placeholder="Loading Market Baskets..."
                      searchPlaceholder="Search..."
                      getDisplayValue={(item: any) => item?.name || ""}
                      getSearchValue={(item: any) => item?.name || ""}
                      disabled={true}
                    ></SearchableListbox>
                  ) : (
                    <SearchableListbox
                      value={selectedMarketBasket}
                      onChange={(marketBasket: any) => {
                        updateParam("marketBasketId", marketBasket?.id || 0);
                        setSelectedMarketBasketName(marketBasket?.name);
                      }}
                      placeholder="Select a Market Basket..."
                      searchPlaceholder="Search..."
                      getDisplayValue={(item: any) => item?.name || ""}
                      getSearchValue={(item: any) => item?.name || ""}
                      disabled={marketShareBasketsLoading}
                    >
                      {marketShareBaskets?.map((marketBasket) => (
                        <SearchableListboxOption
                          key={marketBasket.id}
                          value={marketBasket}
                        >
                          {marketBasket.name}
                        </SearchableListboxOption>
                      ))}
                    </SearchableListbox>
                  )}
                  {marketShareBasketsLoading && (
                    <div className="mt-1">
                      <Loader />
                    </div>
                  )}
                </Field>
              )}

              <Field label="State">
                {statesLoading ? (
                  <SearchableListbox
                    value={null}
                    onChange={() => {}}
                    placeholder="Loading states..."
                    searchPlaceholder="Search states..."
                    getDisplayValue={(item: any) => item?.label || ""}
                    getSearchValue={(item: any) => item?.label || ""}
                    disabled={true}
                  ></SearchableListbox>
                ) : (
                  <SearchableListbox
                    value={selectedState}
                    onChange={(state: any) =>
                      updateParam("stateCode", state?.code || "")
                    }
                    placeholder="Select a state..."
                    searchPlaceholder="Search states..."
                    getDisplayValue={(item: any) => item?.label || ""}
                    getSearchValue={(item: any) => item?.label || ""}
                    disabled={statesLoading}
                  >
                    {states?.map((state) => (
                      <SearchableListboxOption key={state.code} value={state}>
                        {state.label}
                      </SearchableListboxOption>
                    ))}
                  </SearchableListbox>
                )}
                {statesLoading && (
                  <div className="mt-1">
                    <Loader />
                  </div>
                )}
              </Field>

              <Field label="Time Period">
                <SearchableListbox
                  value={selectedTimePeriod}
                  onChange={(period: any) =>
                    updateParam("utilizationPeriod", period?.value || "")
                  }
                  placeholder="Select time period..."
                  searchPlaceholder="Search periods..."
                  getDisplayValue={(item: any) => `Past ${item?.label}` || ""}
                  getSearchValue={(item: any) => item?.label || ""}
                >
                  {TIME_PERIOD_OPTIONS.map((period) => (
                    <SearchableListboxOption key={period.value} value={period}>
                      {period.label}
                    </SearchableListboxOption>
                  ))}
                </SearchableListbox>
              </Field>

              <Field label="Utilization Type">
                {utilizationTypesLoading ? (
                  <SearchableListbox
                    value={null}
                    onChange={() => {}}
                    placeholder="Loading utilization types..."
                    searchPlaceholder="Search types..."
                    getDisplayValue={(item: any) => item?.name || ""}
                    getSearchValue={(item: any) => item?.name || ""}
                    disabled={true}
                  ></SearchableListbox>
                ) : (
                  <SearchableListbox
                    value={selectedUtilizationType}
                    onChange={(type: any) => {
                      updateParam("utilizationTypeId", type?.id ?? 0);
                    }}
                    placeholder="Select utilization type..."
                    searchPlaceholder="Search types..."
                    getDisplayValue={(item: any) => item?.name || ""}
                    getSearchValue={(item: any) => item?.name || ""}
                    disabled={utilizationTypesLoading}
                  >
                    {utilizationTypes?.map((type) => (
                      <SearchableListboxOption key={type.id} value={type}>
                        {type.name}
                      </SearchableListboxOption>
                    ))}
                  </SearchableListbox>
                )}
                {utilizationTypesLoading && (
                  <div className="mt-1">
                    <Loader />
                  </div>
                )}
              </Field>
              <div className="flex items-end">
                <Button color="secondary" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
            {showDrugSelection && (
              <p className="mt-2 text-sm text-gray-500">
                Market baskets consist of a manufacturer's tracked drugs.
              </p>
            )}
          </div>

          {canFetchData && (
            <div className="mt-8">
              <SectionHeading level={3}>Market Share Analysis</SectionHeading>
              <Card className="mt-4">
                {dataLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader />
                  </div>
                ) : error ? (
                  <div className="py-12 text-center">
                    <p className="text-red-600">
                      Unable to load market share data
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      Please check your selection and try again. If the problem
                      persists, contact support.
                    </p>
                  </div>
                ) : chartData ? (
                  <div className="space-y-8">
                    {allPeriodsSuppressed && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm text-amber-900">
                          <span className="font-bold">
                            All utilization data is suppressed.
                          </span>{" "}
                          Every drug in this market basket has suppressed
                          utilization in {selectedState?.label} for each of the
                          selected periods, so market share cannot be
                          calculated. The periods below are shown at 0% with
                          hollow markers to indicate suppression rather than
                          absent data.
                        </p>
                      </div>
                    )}
                    <div>
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                          Market Share Trends for All Drugs
                        </h3>
                        <div className="flex items-center gap-3">
                          {chartData.datasets.length > 10 && (
                            <div className="text-sm text-gray-600">
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                {chartData.datasets.length} drugs displayed
                              </span>
                            </div>
                          )}
                          <Button
                            color="secondary"
                            onClick={handleExport}
                            disabled={!canFetchData || isExporting}
                          >
                            <HiDownload />
                            {isExporting ? "Exporting..." : "Export"}
                          </Button>
                        </div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-6">
                        <div className="h-96">
                          <Line data={chartData} options={chartOptions} />
                        </div>
                      </div>
                      {chartData.datasets.length > 10 && (
                        <div className="mt-3 text-sm text-gray-600">
                          <p>
                            💡 Legend hidden due to large number of drugs. Hover
                            over chart lines to see drug names.
                          </p>
                        </div>
                      )}
                    </div>

                    {availableQuarters.length > 0 && (
                      <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
                        <div className="flex flex-col">
                          <div className="mb-4 flex h-16 items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">
                              Quarterly Market Share Distribution
                            </h3>
                            <div className="flex items-center gap-2">
                              <label
                                htmlFor="quarter-select"
                                className="text-sm font-medium text-gray-700"
                              >
                                Quarter:
                              </label>
                              <select
                                id="quarter-select"
                                value={selectedPieChartQuarter}
                                onChange={(e) =>
                                  setSelectedPieChartQuarter(e.target.value)
                                }
                                className="focus:border-primary-500 focus:ring-primary-500 block w-auto rounded-md border border-gray-300 bg-white pl-3 pr-8 py-1.5 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-1"
                              >
                                {availableQuarters.map((quarter) => (
                                  <option key={quarter} value={quarter}>
                                    {quarter}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="flex-1 rounded-lg border border-gray-200 bg-white p-6">
                            {pieChartData ? (
                              <div className="h-96">
                                <Pie
                                  data={pieChartData}
                                  options={pieChartOptions}
                                />
                              </div>
                            ) : selectedQuarterAllSuppressed ? (
                              <div className="flex h-96 items-center justify-center rounded-lg bg-gray-50">
                                <div className="max-w-xs text-center">
                                  <div className="mb-2 text-gray-400">
                                    <svg
                                      className="mx-auto size-12"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1}
                                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                      />
                                    </svg>
                                  </div>
                                  <p className="font-medium text-gray-500">
                                    All data suppressed
                                  </p>
                                  <p className="text-sm text-gray-400">
                                    Every drug in this market basket has
                                    suppressed utilization for{" "}
                                    {selectedPieChartQuarter}, so no market
                                    share can be calculated.
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex h-96 items-center justify-center rounded-lg bg-gray-50">
                                <div className="text-center">
                                  <div className="mb-2 text-gray-400">
                                    <svg
                                      className="mx-auto size-12"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1}
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                      />
                                    </svg>
                                  </div>
                                  <p className="font-medium text-gray-500">
                                    No data available
                                  </p>
                                  <p className="text-sm text-gray-400">
                                    for selected quarter
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <div className="mb-4 flex h-16 items-center">
                            <h3 className="text-lg font-medium text-gray-900">
                              Market Summary
                            </h3>
                          </div>
                          <div className="flex-1 rounded-lg border border-gray-200 bg-white p-6">
                            <div className="flex h-full flex-col space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="rounded-lg bg-slate-50 p-4">
                                  <div className="text-2xl font-bold text-slate-600">
                                    {(() => {
                                      if (!marketShareData?.quarters) return 0;
                                      const uniqueDrugs = new Set<number>();
                                      marketShareData.quarters.forEach(
                                        (quarter) => {
                                          quarter.drugs.forEach((drug) => {
                                            uniqueDrugs.add(drug.drugId);
                                          });
                                        },
                                      );
                                      return uniqueDrugs.size;
                                    })()}
                                  </div>
                                  <div className="text-sm font-medium text-slate-600">
                                    Total Drugs in Market Basket
                                  </div>
                                </div>
                                <div className="rounded-lg bg-emerald-50 p-4">
                                  <div className="text-2xl font-bold text-emerald-600">
                                    {availableQuarters.length}
                                  </div>
                                  <div className="text-sm font-medium text-emerald-600">
                                    Time Periods
                                  </div>
                                </div>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">
                                  <span className="font-bold">Note:</span> Drugs
                                  in market basket that are not shown in
                                  graphs/tables reflect no utilization data
                                  captured. Drugs marked "Suppressed" in the
                                  table, and drawn as hollow points on the line
                                  graph, do have utilization that CMS has
                                  withheld (CMS applies a suppression filter for
                                  all NDCs with less than 11 prescriptions
                                  within a given quarter, within a given state).
                                  Suppressed drugs count as 0% market share
                                  because their volume is unknown.
                                </p>
                              </div>
                              <div className="border-t pt-4">
                                <h4 className="mb-2 text-sm font-bold text-gray-900">
                                  Drugs
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {(() => {
                                    if (!marketShareData?.quarters) return null;
                                    const uniqueDrugs = new Set<string>();
                                    marketShareData.quarters.forEach(
                                      (quarter) => {
                                        quarter.drugs.forEach((drug) => {
                                          uniqueDrugs.add(drug.drugName);
                                        });
                                      },
                                    );
                                    return [...uniqueDrugs]
                                      .sort()
                                      .map((name) => (
                                        <span
                                          key={name}
                                          className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                                        >
                                          {name}
                                        </span>
                                      ));
                                  })()}
                                </div>
                              </div>
                              {dynamicTopPerformer && (
                                <div className="border-t pt-4">
                                  <h4 className="mb-3 text-sm font-medium text-gray-900">
                                    Top Performer - {selectedPieChartQuarter}
                                  </h4>
                                  <div className="rounded-lg bg-gradient-to-r from-slate-50 to-indigo-50 p-4">
                                    <div className="text-lg font-semibold text-gray-900">
                                      <span>
                                        {dynamicTopPerformer.drugName}
                                      </span>
                                    </div>
                                    <div className="text-primary-900 text-2xl font-bold">
                                      <span>
                                        {dynamicTopPerformer.marketShare.toFixed(
                                          1,
                                        )}
                                        %
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      Market Share
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                          CMS State Drug Utilization Data
                        </h3>
                        <div className="text-sm text-gray-500">
                          {selectedState?.label} · Past{" "}
                          {selectedTimePeriod?.label} ·{" "}
                          {selectedUtilizationType?.name} Utilization
                        </div>
                      </div>
                      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Period
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Drug
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Market Share
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Prescriptions
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Units Reimbursed
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {sortedQuarters.flatMap((quarter) => {
                                const period =
                                  quarter.quarter === null
                                    ? `Y${quarter.year}`
                                    : `Q${quarter.quarter} ${quarter.year}`;
                                // Filter to only the drugs visible in the line chart, sorted alphabetically
                                const visibleDrugs = [...quarter.drugs]
                                  .filter(
                                    (d) =>
                                      !hiddenLineChartDrugNames.has(d.drugName),
                                  )
                                  .sort((a, b) =>
                                    a.drugName.localeCompare(b.drugName),
                                  );
                                const visibleTotal = visibleDrugs.reduce(
                                  (sum, d) => sum + (d.marketShare || 0),
                                  0,
                                );

                                return visibleDrugs.map((drug, drugIndex) => {
                                  const isSuppressed = drug.isSuppressionUsed;
                                  const normalizedShare =
                                    visibleTotal === 0
                                      ? 0
                                      : ((drug.marketShare || 0) /
                                          visibleTotal) *
                                        100;
                                  return (
                                    <tr
                                      key={`${quarter.year}-${quarter.quarter}-${drug.drugId}`}
                                      className={
                                        drugIndex > 0 ? "bg-gray-50" : ""
                                      }
                                    >
                                      {drugIndex === 0 && (
                                        <td
                                          className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900"
                                          rowSpan={visibleDrugs.length}
                                        >
                                          {period}
                                        </td>
                                      )}
                                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                        {drug.drugName}
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                        {isSuppressed ? (
                                          "Suppressed"
                                        ) : (
                                          <div className="flex items-center">
                                            <div className="flex-1">
                                              <div className="text-sm font-medium text-gray-900">
                                                {normalizedShare.toFixed(2)}%
                                              </div>
                                              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                                                <div
                                                  className="market-share-bar h-full rounded-full bg-slate-600 transition-all duration-300 ease-in-out"
                                                  style={
                                                    {
                                                      "--bar-width": `${normalizedShare}%`,
                                                    } as React.CSSProperties
                                                  }
                                                ></div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500">
                                        {isSuppressed
                                          ? "Suppressed"
                                          : Math.round(
                                              drug.numberOfPrescriptions ?? 0,
                                            ).toLocaleString()}
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500">
                                        {isSuppressed
                                          ? "Suppressed"
                                          : new Intl.NumberFormat("en-US", {
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 1,
                                              useGrouping: true, // Ensures commas are used for thousands separator
                                            }).format(
                                              drug.unitsReimbursed ?? 0,
                                            )}
                                      </td>
                                    </tr>
                                  );
                                });
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-primary-900 font-bold">
                        What is Suppressed Data?
                      </h5>
                      <p>
                        As CMS is obligated by the Federal Privacy Act, 5 U.S.C.
                        Section 552a and the HIPAA Privacy Rule, 45 C.F.R Parts
                        160 and 164, to protect the privacy of individual
                        beneficiaries and other persons, all direct identifiers
                        have been removed and data that are less than eleven
                        (11) counts are suppressed. Rows marked "Suppressed" in
                        the Market Share, Prescriptions and Units Reimbursed
                        columns indicate suppressed data. CMS applies counter or
                        secondary
                        suppression in cases where only one prescription is
                        suppressed for primary reasons (e.g., one prescription
                        in a state). Also, if one sub-group (e.g., number of
                        prescriptions) is suppressed, then the other sub-groups
                        are suppressed.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <div className="mx-auto max-w-md">
                      <div className="mb-4 text-gray-400">
                        <svg
                          className="mx-auto size-16"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">
                        No Market Share Data Available
                      </h3>
                      <p className="mb-6 text-sm text-gray-600">
                        No market share data is available with the selected
                        criteria. Try adjusting your selection.
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}

          {!canFetchData && (
            <div className="mt-8">
              <SectionHeading level={3}>Market Share Analysis</SectionHeading>
              <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-12 text-center dark:border-zinc-700 dark:bg-zinc-900/50">
                <h3 className="mb-4 text-lg font-semibold text-zinc-900 dark:text-white">
                  Select Options to View Report
                </h3>
                <p className="mb-6 text-zinc-500 dark:text-zinc-400">
                  Choose a market basket, state, time period, and utilization
                  type to generate the market share analysis.
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
                  {params.marketBasketId === 0 && showDrugSelection && (
                    <span className="rounded bg-yellow-100 px-2 py-1 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                      Market basket required
                    </span>
                  )}
                  {!params.stateCode && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                      State required
                    </span>
                  )}
                  {!params.utilizationPeriod && (
                    <span className="rounded bg-red-100 px-2 py-1 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      Time period required
                    </span>
                  )}
                  {!hasValidUtilizationType && (
                    <span className="rounded bg-red-100 px-2 py-1 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      Utilization type required
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
